import type { TrackedTranslation } from '@ps/web-core-intl';
import type { InputProps } from '@ps/web-shared-components';
import { minLength } from '@ps/web-shared-validators';
import { getCountryCallingCode } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import React from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import type { CardType } from '../../types/cardType';
import PaymentMethodLogo from '../PaymentMethodLogo/PaymentMethodLogo';
import TextInput from '../TextInput/TextInput';

import {
  TXTCLI_Mobile_Phone_Number,
  TXTCLI_Please_enter_your_phone_number
} from './Phone.messages.json';

interface Props extends Omit<InputProps, 'label'> {
  disablePrefix?: boolean;
  label?: TrackedTranslation;
  cardType?: CardType;
}

const phoneMinLength = 5;

const Phone: React.FC<Props> = ({
  disablePrefix = false,
  label = TXTCLI_Mobile_Phone_Number,
  cardType,
  ...rest
}) => {
  const countryCode = useSelector(select.account.getCountry);
  const code = countryCode && getCountryCallingCode(countryCode as CountryCode);

  const prefix = !disablePrefix && (code as string);

  const parse = (value: string) => {
    const parsedValue = value.replace(/[^0-9]/g, '');
    if (prefix) {
      return prefix + parsedValue;
    }
    return parsedValue;
  };

  const format = (value: string) => {
    if (!value) return '';
    if (prefix) {
      return value.replace(new RegExp(`^${prefix}`, 'g'), '');
    }
    return value;
  };

  return (
    <TextInput
      label={label}
      format={format}
      parse={parse}
      validators={[
        minLength(
          prefix ? prefix.length + phoneMinLength : phoneMinLength,
          TXTCLI_Please_enter_your_phone_number
        )
      ]}
      {...(cardType && {
        startAdornment: prefix ? 'logoAndPrefix' : 'logo',
        icon: <PaymentMethodLogo cardType={cardType} variant="small" />
      })}
      type="tel"
      {...(prefix && { prefix, startAdornment: cardType ? 'logoAndPrefix' : 'prefix' })}
      {...rest}
    />
  );
};

export default Phone;
